// Global
import React, { useState, useCallback, useMemo } from 'react';

// Local
import styles from './AlertBanner.module.scss';
import { Themes } from 'lib/themes';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { getCookie, setCookie } from 'lib/utils/cookies';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import useDictionary from 'lib/hooks/useDictionary';
export interface AlertBannerProps {
  title?: React.ReactNode;
  text?: React.ReactNode;
  icon?: React.ReactNode;
  hideIcon?: boolean;
  primaryCta?: React.ReactNode;
  secondaryCta?: React.ReactNode;
  startDate?: string;
  endDate?: string;
  theme?: Themes;
  onDismiss?: () => void;
  isDismissed?: boolean;
  bannerItemId?: string;
}

export const AlertBanner = (props: AlertBannerProps) => {
  const [isClientDismissed, setIsClientDismissed] = useState(false);
  const bannerItemId = props.bannerItemId || 'default';

  const bannerShouldShow = useMemo(() => {
    //check if a date is empty/default from Sitecore
    const isEmptyDate = (dateValue?: string) => {
      return !dateValue || dateValue === '0001-01-01T00:00:00Z' || dateValue === '';
    };

    const now = new Date(Date.now());

    // Show banner if current time is within the valid date range (or if dates are empty)
    const hasValidStartDate =
      isEmptyDate(props.startDate) || (props.startDate && now >= new Date(props.startDate));
    const hasValidEndDate =
      isEmptyDate(props.endDate) || (props.endDate && now <= new Date(props.endDate));

    const shouldHide = props.isDismissed || isClientDismissed;
    const hasContent = props.text || props.title;
    return !shouldHide && hasContent && hasValidStartDate && hasValidEndDate;
  }, [
    props.startDate,
    props.endDate,
    props.isDismissed,
    props.text,
    props.title,
    isClientDismissed,
  ]);

  const handleDismiss = useCallback(() => {
    setIsClientDismissed(true);

    const existingIds = getCookie('alert-banner-dismissed-ids');
    const dismissedIdsArray = existingIds ? existingIds.split(',') : [];

    // Add current banner ID if not already in the list
    if (!dismissedIdsArray.includes(bannerItemId)) {
      dismissedIdsArray.push(bannerItemId);
    }

    // Save updated array back to cookie
    setCookie('alert-banner-dismissed-ids', dismissedIdsArray.join(','));

    if (props.onDismiss) {
      props.onDismiss();
    }
  }, [bannerItemId, props]);

  const { getDictionaryValue } = useDictionary();

  // Don't render if dismissed, if text is empty, or if not within valid date range
  if (!bannerShouldShow) {
    return null;
  }

  return (
    <BrandAndThemeProvider theme={props.theme}>
      <div
        id="alert-banner"
        data-component="authorable/shared/content/alert-banner/alert-banner"
        className={styles.alertBanner}
        role="banner"
        aria-live="polite"
        suppressHydrationWarning
      >
        <div className={styles.banner}>
          <div className={styles.container}>
            {!props.hideIcon && (
              <div className={styles.iconContainer}>
                {props.icon ?? <SvgIcon icon="info" size="md" />}
              </div>
            )}
            <div className={styles.content}>
              <div className={styles.textContainer}>
                {props.title && <h2 className={styles.title}>{props.title}</h2>}
                {props.text && <div className={styles.text}>{props.text}</div>}
              </div>
              {(props.primaryCta || props.secondaryCta) && (
                <div className={styles.ctaContainer}>
                  {props.primaryCta}
                  {props.secondaryCta}
                </div>
              )}
            </div>
          </div>
        </div>
        <CtaButton
          type="button"
          className={styles.closeButton}
          onClick={handleDismiss}
          aria-label={getDictionaryValue('Common.AlertBanner.CloseBanner') || 'Close alert banner'}
          ctaStyle="none"
          ctaIconSize="xs"
        >
          <SvgIcon icon="close" size="xs" />
        </CtaButton>
      </div>
    </BrandAndThemeProvider>
  );
};
